import React, { useEffect, useState, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useGetAllCountriesQuery } from '../../api/endpoints/country';
import { useLanguage } from '../../contexts/LanguageContext';

const CountryDropdown = ({ value, onChange, className, placeholder = 'Select a country', onBlur, disabled = false, languageId = 1 }) => {
    const { getCurrentLanguageId } = useLanguage();

    // Fetch countries using RTK Query
    const { data: countries = [], isLoading } = useGetAllCountriesQuery({ languageId: getCurrentLanguageId() });

    // Transform countries data for dropdown
    const countryOptions = useMemo(() => {
        return countries.map((country) => ({
            id: country.id || country.code || country.name,
            code: country.code,
            name: country.name,
            text: country.name,
            displayValue: country.name
        }));
    }, [countries]);

    // Find the selected country based on the value prop
    const selectedCountry = useMemo(() => {
        if (!value || countryOptions.length === 0) return null;

        if (typeof value === 'string') {
            // If value is a string, find by name/text
            return countryOptions.find((c) => c.name === value || c.text === value || c.displayValue === value) || null;
        } else if (typeof value === 'object' && value !== null) {
            // If value is an object, find by any matching property
            const searchName = value.name || value.text || value.displayValue;
            const searchId = value.id;

            return countryOptions.find((c) => (searchName && (c.name === searchName || c.text === searchName || c.displayValue === searchName)) || (searchId && c.id === searchId)) || null;
        }

        return null;
    }, [value, countryOptions]);

    const handleChange = (e) => {
        // Always return the full object with all properties
        if (onChange) {
            onChange(e.value);
        }
    };

    // Custom option template
    const countryOptionTemplate = (option) => {
        if (!option) return null;
        return (
            <div className="flex align-items-center">
                <span>{option.name}</span>
            </div>
        );
    };

    // Custom value template
    const selectedCountryTemplate = (option, props) => {
        if (option && option.name) {
            return (
                <div className="flex align-items-center">
                    <span>{option.name}</span>
                </div>
            );
        }
        return <span>{props.placeholder}</span>;
    };

    return (
        <Dropdown
            value={selectedCountry}
            onChange={handleChange}
            options={countryOptions}
            optionLabel="name"
            dataKey="name"
            className={className}
            placeholder={placeholder}
            onBlur={onBlur}
            disabled={disabled || isLoading}
            loading={isLoading}
            filter
            filterBy="name"
            showClear
            itemTemplate={countryOptionTemplate}
            valueTemplate={selectedCountryTemplate}
            emptyMessage="No countries found"
            emptyFilterMessage="No countries found"
            panelClassName="country-dropdown-panel"
        />
    );
};

export default CountryDropdown;
